import { FormEvent, FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { AppBar, Button, Dialog, IconButton, Stack, Toolbar, Typography } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import LoadingButton from '@/components/ui/loading-button';
import useToastMessage from '@/shared/context/toast-message-context';
import { Customer, SingleSite } from '@/core/entities';
import { SITE_FORM_KEY } from '@/shared/constants/form-keys';
import { useCreateSite, useUpdateSite } from '@/queries/sites';
import { useTimezones } from '@/queries/timezones';
import { useStates } from '@/queries/states';
import SiteFormDivider from './components/SiteFormDivider';
import SiteFormTransition from './components/SiteFormTransition';
import SectionBasicInformation from './sections/SectionBasicInformation';
import SectionMainPointOfContact from './sections/SectionMainPointOfContact';
import SectionLocationStructure from './sections/SectionLocationStructure';
import { SiteFormModel } from './types';
import {
	makeCreateSiteRequestFromFormModel,
	makeUpdateSiteRequestFromFormModel,
	makeFormModelFromSite,
	getDefaultSiteFormModel,
} from './utils';

interface SiteFormProps {
	open: boolean;
	onClose: () => void;
	customer?: Customer | null;
	onSubmitted?: () => Promise<void> | void;
	site?: SingleSite;
	subtitle?: string;
}

const SiteForm: FunctionComponent<SiteFormProps> = ({
	open,
	onClose,
	customer,
	onSubmitted,
	site,
	subtitle,
}): ReactElement => {
	const { dispatch } = useToastMessage();
	const [formModel, setFormModel] = useState<SiteFormModel>(getDefaultSiteFormModel());
	const [formFile, setFormFile] = useState<File | null>(null);


	useEffect(() => {
		if (site) {
			const formModelfromSite = makeFormModelFromSite(site);
			setFormModel(formModelfromSite);
		} else {
			setFormModel(getDefaultSiteFormModel());
		}
	}, [open, site]);

	const { isLoading: isCreatingCustomer, mutate: onSubmitCreateSite } = useCreateSite({
		onSuccess: async () => {
			await onSubmitted?.();
			onClose();
			dispatch({
				type: 'success',
				content: 'Site successfully created',
			});
		},
	});

	const { isLoading: isUpdatingCustomer, mutate: onSubmitUpdateSite } = useUpdateSite({
		onSuccess: async () => {
			await onSubmitted?.();
			onClose();
			dispatch({
				type: 'success',
				content: 'Site successfully updated',
			});
		},
	});

	const onSubmit = (event: FormEvent) => {
		event.preventDefault();
		if (site) {
			onSubmitUpdateSite({
				id: site.id,
				request: {
					request: makeUpdateSiteRequestFromFormModel(formModel),
					formKey: SITE_FORM_KEY,
				},
			});
		} else {
			onSubmitCreateSite({
				id: customer!.id,
				request: {
					request: makeCreateSiteRequestFromFormModel(formModel),
					formKey: SITE_FORM_KEY,
				},
			});
		}
	};

	const { data: timezones } = useTimezones();
	const { data: states } = useStates();

	const title = useMemo(() => (site ? 'Edit site' : 'Create new site'), [site]);
	const confirmLabel = useMemo(() => (site ? 'Save' : 'Create site'), [site]);
	const disabled = useMemo(() => {
		const mandatoryFields: (keyof SiteFormModel)[] = [
			'name',
			'address',
			'city',
			'state',
			'zip',
			'timeZone',
		];
		const mandatoryFieldsValues: string[] = [
			...Object.entries(formModel)
				.filter(([key]) => mandatoryFields.includes(key as keyof SiteFormModel))
				.map(([, value]) => value as string),
			...formModel.structure.map((level) => level.name),
		];
		return !mandatoryFieldsValues.every((value) => value.length);
	}, [formModel]);

	if (!customer) return <></>;

	return (
		<Dialog open={open} fullScreen TransitionComponent={SiteFormTransition}>
			<AppBar position="relative" color="transparent">
				<Toolbar>
					<IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
						<CloseOutlined />
					</IconButton>
					<Stack direction="column" ml={2}>
						<Typography variant="caption" color="text.secondary">
							{subtitle}
						</Typography>
						<Typography variant="h6">{title}</Typography>
					</Stack>
				</Toolbar>
			</AppBar>

			<Stack component="form" direction="column" padding={4} maxWidth={'664px'} onSubmit={onSubmit}>
				<Stack direction="column">
					<SectionBasicInformation
						formModel={formModel}
						setFormModel={setFormModel}
						timezones={timezones}
						states={states}
					/>
					<SiteFormDivider />
					<SectionMainPointOfContact formModel={formModel} setFormModel={setFormModel} />
					<SiteFormDivider />
					{formFile?.name}
					<SectionLocationStructure
						formModel={formModel}
						setFormModel={setFormModel}
						setFormFile={setFormFile}
					/>
				</Stack>

				<SiteFormDivider />

				<Stack direction="row" justifyContent="start" spacing={2}>
					<Button variant="text" onClick={onClose}>
						Cancel
					</Button>
					<LoadingButton
						type="submit"
						loading={isCreatingCustomer || isUpdatingCustomer}
						variant="contained"
						disabled={disabled}
					>
						{confirmLabel}
					</LoadingButton>
				</Stack>
			</Stack>
		</Dialog>
	);
};

export default SiteForm;
